import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import {
  getAllInPriceFromListPrice,
  getListPriceFromAllinPrice,
} from 'src/utils/inventoryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsPricingSectionUpdates,
  UiCurrency,
} from 'src/WebApiController';

import * as styles from './PricingSetting.css';

type PricingSettingManualPriceProps = {
  uiCurrency: UiCurrency;
  sellerFee: number | null;
};

export const PricingSettingManualPrice: React.FC<
  PricingSettingManualPriceProps
> = ({ uiCurrency, sellerFee }) => {
  const setPriceText = useContent(ContentId.Permission_SetPrice);
  const resetText = useContent(ContentId.Reset);

  const hasConsigneeSingleEntityUpdateFeature = useUserHasFeature(
    Feature.ConsigneeSingleEntityUpdate
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const { selectedListing } = useInventoryEventDetailContext();
  const { updateItemInEvent } = useCatalogDataContext();
  const { onUpdateListing } = useEventHubContext();
  const { showErrorDialog } = useClientLogContext();
  const { watch, reset, formState } =
    useFormContext<ListingDetailsAutoPricingSectionUpdates>();

  const [listPrice, setListPrice] = useState<number | null>(
    selectedListing?.listPrice ?? null
  );

  // This is to ensure that we use the correct list price when switching selected listing
  useEffect(() => {
    if (selectedListing?.listPrice != null) {
      setListPrice(selectedListing?.listPrice);
    }
  }, [selectedListing?.listPrice]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const hasChanges = useMemo(() => {
    return listPrice != (selectedListing?.listPrice ?? null);
  }, [listPrice, selectedListing?.listPrice]);

  const input = watch();

  const hasCompSettingChanges = useMemo(() => {
    const defaultValues = formState.defaultValues;
    return (
      input.compListingExcludeFanInventory !==
        defaultValues?.compListingExcludeFanInventory ||
      input.compListingExcludeDefects !==
        defaultValues?.compListingExcludeDefects ||
      !isEqual(
        input.compListingSelectedSectionSettings,
        defaultValues?.compListingSelectedSectionSettings
      ) ||
      !isEqual(
        input.compListingQuantityFilters,
        defaultValues?.compListingQuantityFilters
      )
    );
  }, [input, formState.defaultValues]);

  const onReset = useCallback(() => {
    reset({ ...(formState.defaultValues ?? {}) });

    setListPrice(selectedListing?.listPrice ?? null);
  }, [formState.defaultValues, reset, selectedListing?.listPrice]);

  const submitPriceChange = useCallback(() => {
    if (!selectedListing || !hasChanges) return;

    tryInvokeApi(
      async () => {
        setIsSubmitting(true);
        const client = new ListingClient(activeAccountWebClientConfig);

        const input: ListingDetailsPricingSectionUpdates = {
          id: selectedListing.id,
          rowVersion: null,
          listPrice,
          allInPrice: null, // will be inferred from list price
          buyerUserId: null,
          marketplacePriceUpdates: [],
          autoPricingEnabled: false, // updating manual price will disable auto price
          netProceedsFloor: selectedListing.procsFloor,
          pricerSellerUserId: selectedListing.pricerId,
          netProceedsCeiling: selectedListing.procsCeil,
          currencyCode: selectedListing.currency,
        };
        const result = hasConsigneeSingleEntityUpdateFeature
          ? await client.updateListingPricingSectionByListingId(input.id, input)
          : await client.updateListingPricingSection(input);
        if (isSuccess(result)) {
          const updated = await client.getListingByListingId(
            selectedListing.id,
            selectedListing as ListingDetails,
            [ListingDetailDataField.Pricing]
          );
          if (updated) {
            updateItemInEvent(updated, ActionOutboxEntityType.Listing);
            onUpdateListing(updated.id);
            setIsSubmitting(false);
          }
        }
      },
      (error) => {
        showErrorDialog('ListingClient.updateListing(prices)', error, {
          trackErrorData: { listingId: selectedListing.id, listPrice },
        });
      }
    );
  }, [
    selectedListing,
    hasChanges,
    activeAccountWebClientConfig,
    listPrice,
    hasConsigneeSingleEntityUpdateFeature,
    updateItemInEvent,
    onUpdateListing,
    showErrorDialog,
  ]);

  return (
    <div className={styles.pricingSettingsBodyInnerLayout}>
      <div className={styles.manualPricingContainer}>
        <PosCurrencyField
          className={styles.textAlignRight}
          uiCurrency={uiCurrency}
          prefixDisplay={
            <span
              className={styles.setPriceText}
            >{`${setPriceText}: ${uiCurrency.sym}`}</span>
          }
          value={getAllInPriceFromListPrice(listPrice, null, sellerFee) ?? ''}
          onChange={(e) => {
            const price = parseFloat(e.target.value);
            if (!isNaN(price)) {
              const listPriceNew = getListPriceFromAllinPrice(
                price,
                null,
                sellerFee
              )!;
              setListPrice(listPriceNew);
            }
          }}
        />
      </div>
      {hasCompSettingChanges && (
        <div className={styles.priceWarning}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.m}
          />
          <span className={styles.statementText}>
            <Content id={ContentId.EnablePricingToPersistSettings} />
          </span>
        </div>
      )}
      <div className={styles.innerLayoutFooter}>
        <Button
          disabled={isSubmitting}
          variant="link"
          textColor="primary"
          justifyContent="start"
          onClick={onReset}
          shape="none"
        >
          {resetText}
        </Button>

        <OkButton
          disabled={isSubmitting || !hasChanges}
          onClick={submitPriceChange}
          textContentId={ContentId.Save}
        />
      </div>
    </div>
  );
};
